<script setup lang="ts">
import type { TooltipItem } from 'chart.js'
import {
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { computed, ref } from 'vue'
import { Bar as BarChart, Line as LineChart } from 'vue-chartjs'

Chart.register(Title, Tooltip, Legend, LineElement, BarElement, LinearScale, PointElement, CategoryScale)

interface SerieInfo {
  label: string
  color?: string
  unit?: string
}

interface SerieInfos {
  [key: string]: SerieInfo
}

interface DataSet {
  name: string
  label: string
  data: (string | number | undefined)[]
  fill: boolean
  borderWidth?: number
  borderColor: string
  backgroundColor: string
  unit?: string
  hidden: boolean
}

interface Props {
  type?: string
  title?: string
  labelSets: string[]
  labelRotation?: number
  serie: Record<string, number>[]
  serieInfos: SerieInfos
  minY?: number
  stepY?: number
  maxY?: number
  labelY?: string
}

const props = withDefaults(defineProps<Props>(), {
  type: 'bar',
  title: undefined,
  labelRotation: undefined,
  minY: undefined,
  stepY: undefined,
  maxY: undefined,
  labelY: undefined,
})

const chart = ref(null)
const hiddenDataSets = ref([])

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    title: { display: false },
    legend: { display: false },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
      callbacks: {
        label: function(context: TooltipItem<'bar' | 'line'>) {
          const label = context.dataset.label
          let value = context.parsed.y
          let unit = (context.dataset as DataSet).unit

          value = typeof (value) === 'undefined' ? 0 : value
          unit = typeof (unit) === 'undefined' ? '' : unit

          return `${label}: ${value} ${unit}`
        }
      }
    }
  },
  hover: { mode: 'nearest' as const, intersect: true },
  scales: {
    x: {
      grid: {
        display: false
      },
      ticks: {
        padding: 10,
        ...(typeof props.labelRotation === 'number' && {
          maxRotation: props.labelRotation,
          minRotation: props.labelRotation
        })
      }
    },
    y: {
      grid: {
        display: true
      },
      ticks: {
        min: props.minY,
        max: props.maxY,
        stepSize: props.stepY,
      },
    },
  }
}))

const dataSets = computed(() => {
  const colorSet = ['#E331A9', '#0E3C6A', '#318BE3', '#31E1E3', '#AC31E3', '#FD6565']
  const datasets: DataSet[] = []

  Object.entries(props.serieInfos).forEach(([name, info]) => {
    datasets.push({
      name: name,
      label: info.label,
      data: props.serie.map((stat) => stat[name]),
      fill: props.type !== 'line',
      borderWidth: props.type === 'line' ? 1 : undefined,
      borderColor: info.color ?? colorSet[datasets.length % colorSet.length],
      backgroundColor: info.color ?? colorSet[datasets.length % colorSet.length],
      unit: info.unit,
      hidden: hiddenDataSets.value.includes(info.label)
    })
  })

  return datasets
})

const chartDatasets = computed(() => ({
  labels: props.labelSets,
  datasets: dataSets.value,
}))

function toggleDataSets(dataSet: DataSet) {
  if (!hiddenDataSets.value.includes(dataSet.label)) {
    hiddenDataSets.value.push(dataSet.label)
  } else {
    hiddenDataSets.value = hiddenDataSets.value.filter((name) => name !== dataSet.label)
  }
}

function getLegendBoxStyle(dataset: DataSet) {
  const style = []

  const color = hiddenDataSets.value.includes(dataset.label) ? 'transparent' : dataset.backgroundColor

  style.push('display: inline-block')
  style.push(`background-color:${color}`)
  style.push('border: 1px solid #C0C0C0')
  style.push('width:15px')
  style.push('height: 15px')

  return style.join(';')
}

function getLegendLabStyle(dataset: DataSet) {
  const style = []

  style.push('display: inline-block')
  style.push('margin-left: 10px;')
  if (dataset.hidden) {
    style.push('text-decoration: line-through;')
  }

  return style.join(';')
}
</script>

<template>
  <div class="stat-graph-container">
    <div
      v-if="title"
      class="row"
    >
      <div class="col-12 stat-graph-title">
        {{ title }}
      </div>
    </div>
    <div
      v-if="labelY"
      class="row p-1 g-0 stat-graph-label"
    >
      ({{ labelY }})
    </div>
    <div class="stat-graph-canvas">
      <BarChart
        v-if="'bar' === type"
        :chart-options="chartOptions"
        :chart-data="chartDatasets"
        :styles="{ height: '210px'}"
      />
      <LineChart
        v-else-if="'line' === type"
        :chart-options="chartOptions"
        :chart-data="chartDatasets"
        :styles="{ height: '210px'}"
      />
    </div>
    <div>
      <div
        v-for="(dataset, idx) in dataSets"
        :key="'stat_datasets_'+idx+'_'+dataset.name"
        class="d-flex flex-row px-2 g-0 align-items-center"
        style="cursor: pointer;"
        @click="toggleDataSets(dataset)"
      >
        <span
          :style="getLegendBoxStyle(dataset)"
        />
        <span
          :style="getLegendLabStyle(dataset)"
        >{{ dataset.label }}</span>
      </div>
    </div>
  </div>
</template>
